import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'

import { generateEnumTranslations } from '@/common/utils/enum'
import { SpatialPolicy as Policy } from '@/models/spatialPolicy'
import {
  AllocationEnum,
  generateEnumDropdownOptions,
  PolicyTypeId,
  VehicleCountEnum,
  VehicleStateGroupEnum,
  VehicleStateGroupType,
} from '@/models/spatialPolicyTypes'
import { applyCitywideOption, getCitywideLayerName } from '@/modules/layers/constants'
import { useAvailableLayerOptions } from '@/modules/layers/queries'
import { useMdsOperators, useOperatorNames } from '@/modules/operator/hooks'
import { useRoutesVehicleTypeOptions } from '@/modules/routes/internal/util'

import { PolicyType } from '../../../utils/types'
import { EditableFieldProps } from '../../PolicyDetails/Form/fields/types'
import { PolicyTypeChip } from '../../PolicyTable/contentCells/TypeCell/PolicyTypeChip'

import { unitOptions } from './fieldOptions'
import { ThemedMultiSelect, ThemedSelect } from './inputs'
import { ThemedField, ValueChips } from './ThemedField'

// The fields below show and hide in the same cases as the old fields in
// `PolicyDetails/Form/fields`. Each one is a select when editable, and chips when not. Fields that
// load their options do it only when editable, so the details page does not load them.

export const PolicyTypeField: FC<EditableFieldProps> = ({
  clearErrors,
  editable,
  editPolicy,
  onValidate,
  policy,
  ...props
}) => {
  const { t } = useTranslation()
  const label = t('policiesLibrary.policyType', 'Policy Type')

  if (!editable)
    return (
      <ThemedField label={label} hidden={!policy.policyType.title}>
        <PolicyTypeChip policyType={policy.policyType} />
      </ThemedField>
    )

  return (
    <ThemedField label={label} {...props}>
      {id => (
        <ThemedSelect
          id={id}
          error={props.error}
          required={props.required}
          onBlur={onValidate}
          onChange={policyType => {
            if (policyType) editPolicy?.(p => p.setPolicyType(policyType as PolicyTypeId))
            clearErrors?.()
          }}
          options={PolicyType.values().map(type => type.dropdownOption)}
          placeholder={t('policiesLibrary.formSelectTypeTitle', 'Select a Policy Type')}
          value={policy.policyType.id}
        />
      )}
    </ThemedField>
  )
}

const ShapeLayerSelect: FC<EditableFieldProps & { id: string }> = ({
  id,
  editPolicy,
  onValidate,
  policy,
  error,
  required,
}) => {
  const { t } = useTranslation()
  const options = applyCitywideOption(useAvailableLayerOptions(), policy.optionalEntireRegion)

  return (
    <ThemedSelect
      id={id}
      error={error}
      required={required}
      onBlur={onValidate}
      onChange={uuid => editPolicy?.(p => p.setShapeLayerUuid(uuid || null))}
      options={options}
      placeholder={t('policiesLibrary.formGeographyPlaceholder', 'Define where the policy applies')}
      value={policy.shapeLayerUUID}
    />
  )
}

export const ShapeLayerField: FC<EditableFieldProps> = props => {
  const { t } = useTranslation()
  const { editable, policy } = props
  const label = t('policiesLibrary.formGeographyTitle', 'Associated Geography')

  // A policy for the entire region has no geography to pick.
  if (policy.isEntireRegion || !editable)
    return (
      <ThemedField label={label} required={editable && props.required}>
        {policy.isEntireRegion ? (
          <ValueChips values={[getCitywideLayerName()]} />
        ) : policy.shapeLayerName ? (
          <ValueChips values={[policy.shapeLayerName]} />
        ) : null}
      </ThemedField>
    )

  return (
    <ThemedField
      label={label}
      error={props.error}
      errorText={props.errorText}
      required={props.required}
    >
      {id => <ShapeLayerSelect id={id} {...props} />}
    </ThemedField>
  )
}

const OperatorsSelect: FC<EditableFieldProps & { id: string }> = ({
  id,
  editPolicy,
  onValidate,
  policy,
  error,
  required,
}) => {
  const { t } = useTranslation()
  const { data, isLoading } = useMdsOperators()

  return (
    <ThemedMultiSelect
      id={id}
      disabled={isLoading}
      error={error}
      required={required}
      onBlur={onValidate}
      onChange={operators => editPolicy?.(p => p.setOperators(operators))}
      options={(data?.items ?? []).map(({ slug, name }) => ({ value: slug, text: name }))}
      placeholder={t('policiesLibrary.formOperatorPlaceholder', 'Select applicable operators')}
      value={policy.operators || []}
    />
  )
}

const OperatorChips: FC<{ operators: string[] }> = ({ operators }) => (
  <ValueChips values={useOperatorNames(operators)} />
)

export const OperatorsField: FC<EditableFieldProps> = props => {
  const { t } = useTranslation()
  const { editable, policy } = props
  const label = t('policiesLibrary.formOperatorsTitle', 'Operators')

  if (!editable)
    return (
      <ThemedField label={label} hidden={_.isEmpty(policy.operators)}>
        {policy.operators && <OperatorChips operators={policy.operators} />}
      </ThemedField>
    )

  return (
    <ThemedField
      label={label}
      error={props.error}
      errorText={props.errorText}
      required={props.required}
    >
      {id => <OperatorsSelect id={id} {...props} />}
    </ThemedField>
  )
}

export const VehicleTypesField: FC<EditableFieldProps> = ({
  editable,
  editPolicy,
  onValidate,
  policy,
  ...props
}) => {
  const { t } = useTranslation()
  const vehicleTypeOptions = useRoutesVehicleTypeOptions()
  const label = t('policiesLibrary.formVehicleTypesTitle', 'Vehicle Types')

  if (!editable)
    return (
      <ThemedField label={label} hidden={_.isEmpty(policy.vehicleTypes)}>
        {policy.vehicleTypes && (
          <ValueChips
            values={policy.vehicleTypes.map(
              type => vehicleTypeOptions.find(option => option.value === type)?.text ?? type
            )}
          />
        )}
      </ThemedField>
    )

  return (
    <ThemedField label={label} {...props}>
      {id => (
        <ThemedMultiSelect
          id={id}
          error={props.error}
          required={props.required}
          onBlur={onValidate}
          onChange={vehicleTypes => editPolicy?.(p => p.setVehicleTypes(vehicleTypes))}
          options={vehicleTypeOptions.filter(option => option.value !== 'all')}
          placeholder={t('policiesLibrary.formVehicleTypesPlaceholder', 'Select applicable modes')}
          value={policy.vehicleTypes || []}
        />
      )}
    </ThemedField>
  )
}

export const VehicleStatesField: FC<EditableFieldProps> = ({
  editable,
  editPolicy,
  onValidate,
  policy,
  ...props
}) => {
  const { t } = useTranslation()
  const label = t('policiesLibrary.formVehicleStatesTitle', 'Vehicle States')

  if (!editable)
    return (
      <ThemedField label={label}>
        {policy.vehicleStateGroup && (
          <ValueChips
            values={[
              generateEnumTranslations(VehicleStateGroupEnum, 'VehicleStateGroupEnum')[
                policy.vehicleStateGroup
              ],
            ]}
          />
        )}
      </ThemedField>
    )

  return (
    <ThemedField label={label} {...props}>
      {id => (
        <ThemedSelect
          id={id}
          error={props.error}
          required={props.required}
          onBlur={onValidate}
          onChange={group =>
            group && editPolicy?.(p => p.setVehicleStatesFromGroup(group as VehicleStateGroupType))
          }
          options={generateEnumDropdownOptions(VehicleStateGroupEnum, 'VehicleStates')}
          placeholder={t('policiesLibrary.formVehicleStatesPlaceholder', 'Select vehicle states')}
          value={policy.vehicleStateGroup}
        />
      )}
    </ThemedField>
  )
}

export const AllocationField: FC<EditableFieldProps> = ({
  editable,
  editPolicy,
  onValidate,
  policy,
  ...props
}) => {
  const { t } = useTranslation()
  const label = t('policiesLibrary.formAllocationTitle', 'Allocation')

  if (!editable)
    return (
      <ThemedField label={label} hidden={!policy.allocation}>
        {policy.allocation && (
          <ValueChips
            values={[
              generateEnumTranslations(AllocationEnum, 'AllocationEnum')[
                policy.allocation as keyof typeof AllocationEnum
              ],
            ]}
          />
        )}
      </ThemedField>
    )

  return (
    <ThemedField label={label} {...props}>
      {id => (
        <ThemedSelect
          id={id}
          error={props.error}
          required={props.required}
          onBlur={onValidate}
          onChange={allocation =>
            editPolicy?.(p => p.setAllocation(allocation as Policy['allocation']))
          }
          options={generateEnumDropdownOptions(AllocationEnum, 'Allocation')}
          placeholder={t(
            'policiesLibrary.formAllocationPlaceholder',
            'Select an allocation method'
          )}
          value={policy.allocation}
        />
      )}
    </ThemedField>
  )
}

export const VehicleCountMethodField: FC<EditableFieldProps> = ({
  editable,
  editPolicy,
  onValidate,
  policy,
  ...props
}) => {
  const { t } = useTranslation()
  const label = t('policiesLibrary.formVehicleCountTitle', 'Vehicle Count Method')

  if (!editable)
    return (
      <ThemedField label={label} hidden={!policy.vehicleCountMethod}>
        {policy.vehicleCountMethod && (
          <ValueChips
            values={[
              generateEnumTranslations(VehicleCountEnum, 'VehicleCountEnum')[
                policy.vehicleCountMethod
              ],
            ]}
          />
        )}
      </ThemedField>
    )

  return (
    <ThemedField label={label} {...props}>
      {id => (
        <ThemedSelect
          id={id}
          error={props.error}
          required={props.required}
          onBlur={onValidate}
          onChange={method =>
            editPolicy?.(p => p.setVehicleCountMethod(method as Policy['vehicleCountMethod']))
          }
          options={generateEnumDropdownOptions(VehicleCountEnum, 'Vehicle')}
          placeholder={t(
            'policiesLibrary.formVehicleCountPlaceholder',
            'Select a vehicle count method'
          )}
          value={policy.vehicleCountMethod}
        />
      )}
    </ThemedField>
  )
}

export const UnitField: FC<EditableFieldProps> = ({
  editable,
  editPolicy,
  onValidate,
  policy,
  ...props
}) => {
  const { t } = useTranslation()
  const label = t('policiesLibrary.formUnitTitle', 'Unit')
  const options = unitOptions(policy)

  if (!editable) {
    const value = policy.unit && options?.find(option => option.value === policy.unit)?.text
    return (
      <ThemedField label={label} hidden={!value}>
        {value && <ValueChips values={[value]} />}
      </ThemedField>
    )
  }

  if (!options) return null

  return (
    <ThemedField label={label} {...props}>
      {id => (
        <ThemedSelect
          id={id}
          error={props.error}
          required={props.required}
          onBlur={onValidate}
          onChange={unit => editPolicy?.(p => p.setUnit(unit as Policy['unit']))}
          options={options}
          value={policy.unit}
        />
      )}
    </ThemedField>
  )
}
